<template>
  <div :class="scope.clsPrefix">
    <!-- 顶部 Tab + 按钮：一直显示（包含创建任务流程中，与内部步骤指示器共存） -->
    <div class="page-header">
      <!-- Tab 切换 -->
      <div class="top-tabs">
        <!-- 市场洞察：洞察报告 / 企业看板 -->
        <template v-if="pageCategory === 'external'">
          <div
            class="top-tab-item"
            :class="{ active: activeTab === 'report' }"
            @click="handleTabChange('report')">
            <span>洞察报告</span>
          </div>
          <div
            class="top-tab-item"
            :class="{ active: activeTab === 'dashboard' }"
            @click="handleTabChange('dashboard')">
            <span>企业看板</span>
          </div>
        </template>
        <!-- 经营分析：经营分析报告 / 决策演练 -->
        <template v-else>
          <div
            class="top-tab-item"
            :class="{ active: activeTab === 'report' }"
            @click="handleTabChange('report')">
            <span>经营分析报告</span>
          </div>
          <div
            class="top-tab-item"
            :class="{ active: activeTab === 'decision' }"
            @click="handleTabChange('decision')">
            <span>决策演练</span>
          </div>
        </template>
      </div>

      <!-- 右侧按钮：创建报告 + 立即对话 -->
      <div class="header-actions">
        <div
          v-if="activeTab === 'report'"
          class="create-report-btn"
          @click="handleOpenCreateTask">
          <i class="el-icon-plus"></i>
          <span>创建报告</span>
        </div>
        <div v-if="pageCategory === 'internal'" class="chat-btn" @click="handleStartChat">
          <i class="el-icon-chat-dot-square"></i>
          <span>立即对话</span>
        </div>
      </div>
    </div>

    <!-- 白色圆角内容容器 -->
    <template v-if="viewState !== 'create'">
      <div class="page-body">
        <!-- 企业看板 -->
        <SmartDashboard
          v-if="activeTab === 'dashboard'"
          @smart-analysis="handleDashboardSmartAnalysis" />

        <!-- 决策演练：四个模块各嵌一个 iframe -->
        <DecisionIntelligence v-else-if="activeTab === 'decision'" />

        <!-- 洞察报告（初始状态：详情 + 列表布局） -->
        <template v-else>
          <div class="split-layout">
            <!-- 左侧：报告详情 -->
            <div class="detail-panel">
              <EnterpriseDetail
                v-if="selectedReportId"
                :id="selectedReportId"
                :key="selectedReportId"
                :room-tag="pageTag"
                @smart-analysis="handleSmartAnalysis"
                @anomaly-detail="openAnomalyDialog"
              />
              <div v-else class="empty-state">暂无数据</div>
            </div>
            <!-- 右侧：报告列表 -->
            <div class="list-panel">
              <div class="list-header">
                <div class="search-area">
                  <el-input
                    size="small"
                    placeholder="搜索标题或摘要..."
                    v-model="searchText"
                    class="search-input">
                  </el-input>
                  <el-button size="small" type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
                </div>
              </div>
              <div class="list-summary">
                <span class="summary-text"><span class="all-report">{{pageTag}}报告</span>（共 {{ total }} 条）</span>
              </div>
              <div
                v-if="reportList.length > 0"
                class="report-list"
                ref="reportList"
                @scroll="handleScroll">
                <div
                  v-for="item in reportList"
                  :key="item.id"
                  class="card-content"
                  :class="{ active: selectedReportId === item.id }"
                  @click="handleReportClick(item)">
                  <div class="content-section">
                    <div class="section-header">
                      <span class="war-section-title">{{ item.title }}</span>
                      <!-- 异常状态角标 -->
                      <span
                        v-if="item.hasAnomaly"
                        class="anomaly-badge-inline"
                        :class="item.anomalyDispositionStatus ? 'anomaly-disposed' : 'anomaly-pending'"
                        @click.stop="openAnomalyDialog(item)"
                      >{{ item.anomalyDispositionStatus ? '已处置' : '待处理' }}</span>
                    </div>
                    <div class="section-desc">{{ item.desc }}</div>
                  </div>
                  <div class="action-buttons" v-if="item.tags && item.tags.length > 0">
                    <button
                      v-for="(btn, bidx) in item.tags"
                      :key="bidx"
                      class="btn-secondary">
                      {{ btn }}
                    </button>
                  </div>
                  <div class="footer-btn">
                    <span class="btn-delete" @click.stop="handleDelete(item)">删除</span>
                    <div v-if="item.hasAnomaly" class="btn-anomaly-detail" @click.stop="openAnomalyDialog(item)">异常详情</div>
                    <div class="btn-primary" @click.stop="handleSmartAnalysis(item)">智能分析</div>
                  </div>
                </div>
                <!-- 底部加载提示 -->
                <div v-if="loadingMore" class="load-tip">
                  <i class="el-icon-loading"></i>
                  <span>加载中...</span>
                </div>
                <div v-else-if="!hasMore" class="load-tip">没有更多了</div>
              </div>
              <div v-else class="empty-state">暂无数据</div>
            </div>
          </div>
        </template>
      </div>
    </template>

    <!-- 创建任务流程视图（按页面类型分流：市场洞察 / 经营分析） -->
    <template v-else>
      <CreateTaskFlowMarket
        v-if="pageCategory === 'external'"
        :page-tag="pageTag"
        :page-category="pageCategory"
        :current-agent-id="currentAgentId"
        @back="viewState = 'initial'"
        @created="fetchReportList"
        @go-automation="handleGoToAutomation" />
      <CreateTaskFlowBusiness
        v-else
        :page-tag="pageTag"
        :page-category="pageCategory"
        :current-agent-id="currentAgentId"
        @back="viewState = 'initial'"
        @created="fetchReportList"
        @go-automation="handleGoToAutomation" />
    </template>

    <!-- AI 分析抽屉：企业看板智能分析触发后弹起，复用 cosmoClaw store 的会话与流式能力 -->
    <el-drawer
      :visible.sync="aiDrawerVisible"
      :with-header="false"
      :modal="false"
      :append-to-body="true"
      size="520px"
      direction="rtl"
      class="enterprise-ai-drawer">
      <div class="enterprise-ai-drawer-body" v-if="aiDrawerVisible">
        <div class="ai-drawer-close-btn" @click="aiDrawerVisible = false">
          <i class="el-icon-close"></i>
        </div>
        <CosmoClawChat mode="assistant" :enlargeable="false" />
      </div>
    </el-drawer>
    <!-- 异常详情弹框 -->
    <AnomalyDetailDialog
      :visible.sync="anomalyDialogVisible"
      :report="anomalyDialogReport"
      @disposed="handleAnomalyDisposed"
    />
  </div>
</template>

<script>
import { getJobReportPageApi, deleteJobReportApi } from '@/api/agentAuth'
import { parseAgentTags } from '@/store/modules/cosmoClaw'
import EnterpriseDetail from './EnterpriseDetail.vue'
import CreateTaskFlowMarket from './CreateTaskFlowMarket.vue'
import CreateTaskFlowBusiness from './CreateTaskFlowBusiness.vue'
import SmartDashboard from './SmartDashboard.vue'
import DecisionIntelligence from './DecisionIntelligence.vue'
import CosmoClawChat from '@/views/smart-workbench/components/CosmoClawChat.vue'
import AnomalyDetailDialog from '@/views/smart-engine-new/agents/AnomalyDetailDialog.vue'

export default {
  name: 'EnterpriseList',
  components: { EnterpriseDetail, CreateTaskFlowMarket, CreateTaskFlowBusiness, SmartDashboard, DecisionIntelligence, CosmoClawChat, AnomalyDetailDialog },

  props: {
    data: {
      type: Object,
      default() {
        return {}
      }
    },
    agentId: {
      type: String,
      default: ''
    }
  },
  inject: ['scrollLoading'],
  computed: {
    currentAgentId() {
      return this.$store.state.cosmoClaw.currentAgentId || this.agentId
    },
    pageTag() {
      if (this.$route.path.includes('marketInsight')) return '市场洞察'
      return '经营分析'
    },
    pageCategory() {
      if (this.$route.path.includes('marketInsight')) return 'external'
      return 'internal'
    }
  },
  data() {
    return {
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-enterprise-list'
      },
      // 顶部 Tab：report 洞察报告 / dashboard 企业看板
      activeTab: 'report',
      // 视图状态：initial 初始列表视图；create 进入创建任务流程（由子组件承载三步内部状态）
      viewState: 'initial',
      // 原始列表相关
      searchText: '',
      filterCategory: '',
      // 分页 + 流式加载相关
      page: 1,
      size: 20,
      total: 0,
      hasMore: true,
      loadingMore: false,
      reportList: [],
      selectedReportId: null,
      loading: false,
      // AI 分析抽屉：组件本地控制，避免被 store 的 sendMessage 内部 SET_DRAWER_VISIBLE(false) 关闭
      aiDrawerVisible: false,
      anomalyDialogVisible: false,
      anomalyDialogReport: null
    }
  },
  created() {
    this.fetchReportList()
  },
  watch: {
    '$route.path'() {
      // 经营分析 / 市场洞察页面切换时，恢复到初始列表视图
      this.viewState = 'initial'
      this.activeTab = 'report'
      this.resetPaging()
      this.fetchReportList()
    },
    '$route.query.agentId'(newVal) {
      this.$store.commit('cosmoClaw/SET_CURRENT_AGENT_ID', newVal)
      this.resetPaging()
      this.fetchReportList()
    }
  },
  methods: {
    // ===== 初始化相关 =====
    // 重置分页状态（路由切换 / 搜索 / 删除后重新加载时调用）
    resetPaging() {
      this.page = 1
      this.hasMore = true
      this.selectedReportId = null
    },
    async fetchReportList(isLoadMore = false) {
      // 加载更多：防止重复触发
      if (isLoadMore) {
        if (!this.hasMore || this.loadingMore) return
        this.loadingMore = true
      } else {
        // 首次加载 / 搜索 / 重置：回到第1页
        this.scrollLoading.open()
        this.page = 1
      }
      const params = {
        page: this.page,
        size: this.size
      }
      if (this.currentAgentId) params.agentId = this.$route.query.agentId || this.currentAgentId
      params.category = this.pageCategory
      if (this.searchText) params.keyword = this.searchText
      try {
        const res = await getJobReportPageApi(params)
        const records = (res && res.data && res.data.records) || []
        const total = (res && res.data && res.data.total) || 0
        if (isLoadMore) {
          // 追加到列表尾部
          this.reportList = this.reportList.concat(this.transformData(records))
        } else {
          this.reportList = this.transformData(records)
          // 首次加载默认选中第一条
          if (this.reportList.length > 0) {
            this.selectedReportId = this.reportList[0].id
          }
        }
        this.total = total
        // 已加载条数 < 总数，说明还有下一页
        this.hasMore = this.reportList.length < total
      } catch (error) {
        console.error('获取报告列表失败:', error)
        if (!isLoadMore) {
          this.reportList = []
          this.total = 0
          this.selectedReportId = null
        }
        // 加载更多失败时不再标记 hasMore，允许用户再次尝试
      } finally {
        this.scrollLoading.close()
        this.loadingMore = false
      }
    },
    // 滚动触底加载下一页
    loadMore() {
      if (!this.hasMore || this.loadingMore || this.loading) return
      this.page++
      this.fetchReportList(true)
    },
    // 列表滚动监听：触底阈值 50px 触发加载
    handleScroll(e) {
      const { scrollTop, scrollHeight, clientHeight } = e.target
      if (scrollHeight - scrollTop - clientHeight < 50) {
        this.loadMore()
      }
    },
    transformData(data) {
      return data.map(item => ({
        id: item.id,
        title: item.title,
        desc: item.summary || item.content || '',
        content: item.content || '',
        tags: item.tags ? item.tags.split(',').slice(0, 2) : [],
        reportType: item.reportType,
        category: item.category,
        hasAnomaly: item.hasAnomaly,
        anomalyDispositionStatus: item.anomalyDispositionStatus
      }))
    },
    // ===== 立即对话（就地唤起宿主侧边聊天抽屉） =====
    // 不复用驾驶舱的 window.openDrawer：那个会新建会话并跳到会话页，用户会离开经营分析。
    // 走本页已有的 smart-analysis 通道（宿主在 <router-view @smart-analysis> 上接收，
    // 见 smart-engine-new/index.vue），宿主侧 handleSmartAnalysis 支持
    // { silentOpenDrawer: true }：仅把侧边聊天抽屉打开，不触发任何分析、不跳页、不新建会话
    // （首次发消息时 CosmoClawInput 会自己建会话）。
    handleStartChat() {
      this.$emit('smart-analysis', { silentOpenDrawer: true })
    },
    // 确保 store 里有当前智能体（原先写在 handleDashboardSmartAnalysis 里）
    _ensureCurrentAgent() {
      const store = this.$store
      if (store.state.cosmoClaw.currentAgentName) return
      const agentId = this.currentAgentId
      let resolvedName = ''
      let resolvedId = ''
      if (agentId) {
        const agentList = store.state.user.agentList || []
        const match = agentList.find((a) => String(a.id) === String(agentId))
        if (match) {
          const { internalName } = parseAgentTags(match.tags, match.name)
          if (internalName) {
            resolvedName = internalName
            resolvedId = String(match.id)
          }
        }
      }
      if (!resolvedName) {
        const agentList = store.state.user.agentList || []
        const firstDh = agentList.find((a) => {
          const { type } = parseAgentTags(a.tags, a.name)
          return type === 'digital-human'
        })
        if (firstDh) {
          const { internalName } = parseAgentTags(firstDh.tags, firstDh.name)
          resolvedName = internalName || 'ceo_shu_zi_ren'
          resolvedId = String(firstDh.id)
        } else {
          resolvedName = 'ceo_shu_zi_ren'
        }
      }
      store.commit('cosmoClaw/SET_CURRENT_AGENT_NAME', {
        agentName: resolvedName,
        agentId: resolvedId
      })
    },
    // ===== 视图切换：进入创建任务流程（子组件自管理三步状态） =====
    handleOpenCreateTask() {
      this.viewState = 'create'
    },
    // ===== 顶部 Tab 切换 =====
    handleTabChange(tab) {
      if (this.activeTab === tab) return
      this.activeTab = tab
      // 切换到洞察报告时，重置视图状态并刷新列表
      if (tab === 'report') {
        this.viewState = 'initial'
        this.fetchReportList()
      }
    },

    // ===== 报告列表相关 =====
    handleSearch() {
      // 搜索：重置分页状态，从第1页重新加载
      this.resetPaging()
      this.fetchReportList()
    },
    handleReportClick(item) {
      this.selectedReportId = item.id
    },
    openAnomalyDialog(item) {
      this.anomalyDialogReport = item
      this.anomalyDialogVisible = true
    },
    handleAnomalyDisposed() {
      this.fetchReportList()
    },
    handleDelete(item) {
      this.$confirm('确定要删除该报告吗？', '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(async () => {
          try {
            await deleteJobReportApi(item.id)
            this.$message.success('删除成功')
            this.fetchReportList()
          } catch (error) {
            console.error('删除报告失败:', error)
            this.$message.error('删除失败')
          }
        })
        .catch(() => {})
    },
    handleSmartAnalysis(item) {
      const store = this.$store
      if (!store.state.cosmoClaw.currentAgentName) {
        const agentId = this.currentAgentId
        let resolvedName = ''
        let resolvedId = ''
        if (agentId) {
          const agentList = store.state.user.agentList || []
          const match = agentList.find((a) => String(a.id) === String(agentId))
          if (match) {
            const { internalName } = parseAgentTags(match.tags, match.name)
            if (internalName) {
              resolvedName = internalName
              resolvedId = String(match.id)
            }
          }
        }
        if (!resolvedName) {
          const agentList = store.state.user.agentList || []
          const firstDh = agentList.find((a) => {
            const { type } = parseAgentTags(a.tags, a.name)
            return type === 'digital-human'
          })
          if (firstDh) {
            const { internalName } = parseAgentTags(firstDh.tags, firstDh.name)
            resolvedName = internalName || 'ceo_shu_zi_ren'
            resolvedId = String(firstDh.id)
          } else {
            resolvedName = 'ceo_shu_zi_ren'
          }
        }
        store.commit('cosmoClaw/SET_CURRENT_AGENT_NAME', {
          agentName: resolvedName,
          agentId: resolvedId
        })
      }
      this.$emit('smart-analysis', {
        type: 'enterpriseWarRoom',
        content: item.content
      })
    },

    // ===== 企业看板智能分析 =====
    // SmartDashboard emit 出来的 { type, scene, content }：
    // - 先解析并设置当前智能体（复用 handleSmartAnalysis 的逻辑）
    // - 打开 AI 抽屉、创建全新会话、发送消息触发流式输出
    async handleDashboardSmartAnalysis(payload) {
      // 1. 确保 currentAgentName/agentId 已设置
      this._ensureCurrentAgent()
      // 2、3. 打开 AI 抽屉（组件本地 state，不被 store 的 sendMessage 内部关闭逻辑影响）并强制新建会话
      const sessionKey = await this._openAiDrawer()
      // 4. 发送消息（触发流式输出），延迟与其他页面智能分析一致，避免消息落到切换前的会话
      setTimeout(() => {
        this.$store.dispatch('cosmoClaw/sendMessage', {
          content: payload?.content || '请基于当前企业信息生成分析',
          sessionKey
        })
      }, 500)
    },

    // 打开本页 AI 抽屉并强制新建会话（每次点击都不复用已有会话，与其他页面智能分析一致）
    async _openAiDrawer() {
      this.aiDrawerVisible = true
      await this.$nextTick()
      return await this.$store.dispatch('cosmoClaw/createNewSession', {
        agentName: this.$store.state.cosmoClaw.currentAgentName,
        forceNew: true
      })
    },

    // ===== 导航 =====
    handleGoToAutomation() {
      const agentId = this.$route.query.agentId || this.currentAgentId
      this.$router.push({
        path: '/smart-engine/task-list',
        query: agentId ? { agentId } : {}
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.enterprise-ai-drawer {
  ::v-deep .el-drawer__body {
    height: 100%;
    padding: 0;
  }
}
.enterprise-ai-drawer-body {
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}
.ai-drawer-close-btn {
  position: absolute;
  top: 10px;
  left: -36px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  color: #606266;
  font-size: 16px;
  transition: all 0.2s;
  &:hover {
    color: #303030;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
}
.all-report {
  font-weight: 600;
  font-size: 14px;
  color: #000000;
  margin-right: 4px;
}
$class-prefix: "rvc-cosmoplat-workspace-enterprise-list";
.#{$class-prefix} {
  height: 100%;
  border-radius: 8px;
  padding: 24px;
  min-height: 500px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
/* ========== 顶部区域 ========== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding-bottom: 12px;
  /* Tab 下分隔线：1px #ddd、透明度 0.6 */
  border-bottom: 1px solid rgba(221, 221, 221, 0.6);
  margin-bottom: 28px;
  background: transparent;
}
/* 顶部 Tab 切换（下划线风格） */
.top-tabs {
  display: flex;
  gap: 28px;
  background: transparent;
  padding: 0;
  border-radius: 0;
}
.top-tab-item {
  position: relative;
  display: flex;
  align-items: center;
  padding: 4px 0;
  font-size: 20px;
  color: #808080;
  cursor: pointer;
  transition: color 0.2s;
  font-weight: 500;
  white-space: nowrap;
}
.top-tab-item:hover {
  color: #4A5FF3;
}
.top-tab-item.active {
  color: #1f2329;
  color: #333;
  font-weight: 600;
}
.top-tab-item.active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -12px;
  height: 4px;
  background: #4A5FF3;
  border-radius: 2px;
}
/* 右侧按钮组 */
.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
/* 创建报告：描边按钮，仅报告 Tab 下显示 */
.create-report-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 14px;
  border: 1px solid #4a5ff3;
  border-radius: 4px;
  font-size: 12px;
  color: #4a5ff3;
  cursor: pointer;
  white-space: nowrap;
  // background: #fff;
  transition: all 0.2s;
}
.create-report-btn:hover {
  // background: #f0f2ff;
}
.create-report-btn i {
  font-size: 14px;
}
/* 立即对话：蓝色实心主按钮 */
.chat-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 14px;
  border: 1px solid #4a5ff3;
  border-radius: 4px;
  font-size: 12px;
  color: #fff;
  cursor: pointer;
  white-space: nowrap;
  background: #4a5ff3;
  transition: all 0.2s;
}
.chat-btn:hover {
  background: #3a4de2;
  border-color: #3a4de2;
}
.chat-btn i {
  font-size: 13px;
}
/* ========== 白色圆角内容容器 ========== */
.page-body {
  flex: 1;
  min-height: 0;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
/* page-body 直接子元素（SmartDashboard / DecisionIntelligence / split-layout）
   在 flex column 容器内需要 flex:1 + min-height:0 才能正确填充 */
.page-body > .smart-dashboard,
.page-body > .decision-intelligence,
.page-body > .split-layout {
  flex: 1;
  min-height: 0;
}
/* SmartDashboard / DecisionIntelligence 自身是纵向布局，这里明确一下
   split-layout 保持默认 row（横向分栏），不要改成 column */
.page-body > .smart-dashboard,
.page-body > .decision-intelligence {
  display: flex;
  flex-direction: column;
}
/* 历史报告返回标签 */
.back-label {
  font-size: 12px;
  color: #4a5ff3;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}
.split-layout {
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 10px;
}
.detail-panel {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}
.list-panel {
  width: 380px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  padding-top: 8px;
  padding-bottom: 24px;
}
.list-header {
  margin-bottom: 16px;
}
.search-area {
  display: flex;
  align-items: center;
  gap: 12px;
}
.search-input {
  flex: 1;
}
.list-summary {
  margin-bottom: 16px;
}
.summary-text {
  padding-left: 20px;
  font-size: 14px;
  color: #999;
  font-family: PingFangSC-Regular;
}
.report-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
  scrollbar-width: thin;
  scrollbar-color: #ccc transparent;
}
.report-list::-webkit-scrollbar {
  width: 4px;
}
.report-list::-webkit-scrollbar-track {
  background: transparent;
}
.report-list::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}
.card-content {
  padding: 16px;
  height: 138px;
  background: #ffffffb3;
  border-radius: 8px;
  border: 1px solid transparent;
  box-shadow: 0 0 8px 0 #005ddd29;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  &:hover {
    background: #f0f4ff;
  }
  &.active {
    border-color: #4A5FF3;
    background: #f0f4ff;
  }
}
.content-section {
  display: flex;
  flex-direction: column;
}
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.war-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  font-family: PingFangSC-Medium;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.section-desc {
  font-size: 12px;
  color: #999;
  margin-top: 6px;
  font-family: PingFangSC-Regular;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
}
.action-buttons {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}
.btn-secondary {
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 3px 8px;
  font-size: 12px;
  color: #666;
  background: #fff;
  cursor: default;
  white-space: nowrap;
}
.footer-btn {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}
.btn-delete {
  font-size: 12px;
  color: #94a3b8;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
    color: #64748b;
  }
}
.btn-primary {
  width: 64px;
  height: 24px;
  background-image: linear-gradient(270deg, #4A5FF3 0%, #4E86FF 100%);
  border-radius: 12px;
  font-size: 12px;
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
/* 异常状态角标 */
.anomaly-badge-inline {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 10px;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 6px;
  &.anomaly-pending {
    background: #fef2f2;
    color: #ef4444;
    border: 1px solid #fecaca;
  }
  &.anomaly-disposed {
    background: #f0fdf4;
    color: #16a34a;
    border: 1px solid #bbf7d0;
  }
}
/* 异常详情按钮 */
.btn-anomaly-detail {
  font-size: 12px;
  color: #ef4444;
  background: transparent;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 2px 10px;
  height: 24px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: #fef2f2;
  }
}
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #999;
  font-size: 14px;
}
/* 流式加载底部提示 */
.load-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 0 4px;
  font-size: 12px;
  color: #999;
  flex-shrink: 0;
  i {
    font-size: 14px;
  }
}
</style>
